/**
 * Changelog state for the portal.
 *
 * Module-level refs rather than a Pinia store, matching `composables/profile.js`
 * — this is one small payload fetched once per session, not cross-component
 * state that needs devtools or hydration.
 *
 * Every call swallows its errors. Nobody's session should break because the
 * release notes could not be fetched.
 */
import ChangelogRepository from '@/features/changelog/api/ChangelogRepository';
import { computed, ref } from 'vue';

const releases = ref([]);
const versions = ref({});
const unread = ref(false);
const showDialog = ref(false);
const isLoaded = ref(false);
const isLoading = ref(false);
const loadError = ref(false);
const isSidebarOpen = ref(false);
let requestId = 0;

export function openChangelog() {
  isSidebarOpen.value = true;
}

export function closeChangelog() {
  isSidebarOpen.value = false;
}

export function toggleChangelog() {
  isSidebarOpen.value = !isSidebarOpen.value;
}

/**
 * Pick the text for a locale out of a backend `{ en, nl, ... }` map.
 *
 * Same convention and fallback order as `useNotificationStrings`: exact locale,
 * then the bare language, then English, then Dutch. A half-translated release
 * still renders rather than showing a blank line.
 *
 * @param {Object<string, string>} values
 * @param {string} locale
 * @returns {string}
 */
export function pickLocalized(values, locale) {
  if (!values || typeof values !== 'object') return '';
  const localeKey = String(locale || '')
    .split('-')[0]
    .toLowerCase();
  const selected = values[localeKey] || values[locale] || values.en || values.nl;
  return typeof selected === 'string' ? selected : '';
}

/**
 * Load the changelog for the signed-in user.
 * @param {{ surface?: string, limit?: number }} [options]
 */
export async function loadChangelog({ surface, limit } = {}) {
  // A delayed login refresh must retain all product tabs while the drawer is open.
  if (isSidebarOpen.value) { surface = undefined; limit = 100; }
  const currentRequest = ++requestId;
  isLoading.value = true;
  loadError.value = false;
  try {
    const params = { ...(surface ? { surface } : {}), ...(limit ? { limit } : {}) };
    const response = await ChangelogRepository.getChangelog(params);
    if (currentRequest !== requestId) return releases.value;
    const data = response?.data || {};
    releases.value = Array.isArray(data.releases) ? data.releases : [];
    versions.value = data.versions || {};
    unread.value = Boolean(data.unread);
    showDialog.value = Boolean(data.show_dialog);
    isLoaded.value = true;
  } catch {
    if (currentRequest === requestId) loadError.value = true;
  } finally {
    if (currentRequest === requestId) isLoading.value = false;
  }
  return releases.value;
}

/**
 * Mark the current versions as seen and clear the dialog + dot.
 *
 * Optimistic: the UI updates immediately and the write is best-effort. Worst
 * case the dialog reappears on the next login, which is far better than a
 * spinner on a dismiss button.
 *
 * @param {Object<string, string>} [only] - limit to these surfaces.
 */
export async function markChangelogSeen(only) {
  showDialog.value = false;
  unread.value = false;
  try {
    await ChangelogRepository.markSeen(only || versions.value);
  } catch {
    // best-effort
  }
}

export function useChangelog() {
  return {
    releases: computed(() => releases.value),
    versions: computed(() => versions.value),
    unread: computed(() => unread.value),
    showDialog: computed(() => showDialog.value),
    isLoaded: computed(() => isLoaded.value),
    isLoading: computed(() => isLoading.value),
    loadError: computed(() => loadError.value),
    isSidebarOpen: computed(() => isSidebarOpen.value),
    latestRelease: computed(() => releases.value[0] || null),
    loadChangelog,
    markChangelogSeen,
    openChangelog,
    closeChangelog,
    toggleChangelog,
  };
}

/** Reset module state. Used on logout and by tests. */
export function resetChangelog() {
  requestId += 1;
  releases.value = [];
  versions.value = {};
  unread.value = false;
  showDialog.value = false;
  isLoaded.value = false;
  isLoading.value = false;
  loadError.value = false;
  closeChangelog();
}
